// Word-level text diff for comparing saved versions of a report section.
//
// Written by hand rather than pulled from npm: the only consumer is the section
// version viewer, and a dependency-free LCS is small enough to keep the bundle and
// the audit surface of a clinical app unchanged.

// Whitespace is kept as its own token so paragraphs, bullets and line breaks survive
// the round trip — the diff has to render with the same shape the clinician typed.
const tokenize = (text) => String(text ?? '').split(/(\s+)/).filter((token) => token !== '');

// Guard on the O(n*m) table. Measured on this shape of input, time is never the
// binding constraint (16M cells diffs in ~50ms) — transient memory is, at 2 bytes a
// cell. 16M cells caps a worst-case diff around 32MB and comfortably covers a
// ~2000-word section, far past anything the AI generates here. Beyond that we stop
// trying to be clever and report a wholesale replacement.
const MAX_MATRIX_CELLS = 16000000;

// Collapse a token-level op stream into runs, so the renderer emits one span per
// changed phrase instead of one per word.
const coalesce = (ops) => {
  const segments = [];
  for (const { type, token } of ops) {
    const last = segments[segments.length - 1];
    if (last && last.type === type) {
      last.text += token;
    } else {
      segments.push({ type, text: token });
    }
  }
  return segments;
};

/**
 * Diff two blocks of text at word granularity.
 *
 * @returns {Array<{type: 'equal'|'added'|'removed', text: string}>} in reading order,
 *   with removals emitted before the additions that replaced them.
 */
export const diffWords = (oldText, newText) => {
  const a = tokenize(oldText);
  const b = tokenize(newText);

  // Identical head and tail are the overwhelming majority of a typical edit; peeling
  // them off first keeps the LCS table small enough to matter.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;

  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const prefix = a.slice(0, start).map((token) => ({ type: 'equal', token }));
  const suffix = a.slice(endA).map((token) => ({ type: 'equal', token }));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  let middle;
  if (midA.length === 0) {
    middle = midB.map((token) => ({ type: 'added', token }));
  } else if (midB.length === 0) {
    middle = midA.map((token) => ({ type: 'removed', token }));
  } else if (midA.length * midB.length > MAX_MATRIX_CELLS) {
    middle = [
      ...midA.map((token) => ({ type: 'removed', token })),
      ...midB.map((token) => ({ type: 'added', token })),
    ];
  } else {
    const m = midA.length;
    const n = midB.length;

    // dp[i][j] = length of the longest common subsequence of midA[i:] and midB[j:].
    // Uint16 cannot overflow here: a cell never exceeds min(m, n), and MAX_MATRIX_CELLS
    // already rejects any input where both sides could reach 65535 tokens.
    const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
    for (let i = m - 1; i >= 0; i -= 1) {
      for (let j = n - 1; j >= 0; j -= 1) {
        dp[i][j] = midA[i] === midB[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }

    middle = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
      if (midA[i] === midB[j]) {
        middle.push({ type: 'equal', token: midA[i] });
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        // Removal before addition, so a modified phrase reads as the old text struck
        // through and the new text highlighted immediately after it.
        middle.push({ type: 'removed', token: midA[i] });
        i += 1;
      } else {
        middle.push({ type: 'added', token: midB[j] });
        j += 1;
      }
    }
    while (i < m) {
      middle.push({ type: 'removed', token: midA[i] });
      i += 1;
    }
    while (j < n) {
      middle.push({ type: 'added', token: midB[j] });
      j += 1;
    }
  }

  return coalesce([...prefix, ...middle, ...suffix]);
};

export const hasDifferences = (oldText, newText) =>
  diffWords(oldText, newText).some((segment) => segment.type !== 'equal');
